import { createSlice } from "@reduxjs/toolkit";

type redirectionURL = {
  [key: string]: {
    link: string;
    label: string;
    name?: string;
    pricing?: any;
    config_images?: Array<any>;
  };
};

export type actionPayload = {
  id: number;
  name: string;
  level: string;
  config_id: number;
  meta: { [key: string]: any };
  pricing: { [key: string]: any };
  config_name: string;
  label: string;
  key: string;
  arrival_date?: string;
  custom_duration?: string;
  link: string | null;
};

type InventoryData = {
  id: number;
  enquired: boolean;
  book_form_initiated: actionPayload | null;
  book_form_completed: actionPayload | null;
  payment_initiated: actionPayload | null;
  payment_completed: actionPayload | null;
  children: {
    [key: string]: {
      id: number;
      enquired: boolean;
      label?:
        | "Book"
        | "Continue to Book"
        | "Continue to Pay"
        | "View Bookings"
        | "Join Waitlist"
        | "Waitlisted";
      book_form_initiated: boolean;
      book_form_completed: boolean;
      payment_initiated: boolean;
      payment_completed: boolean;
      link: string;
      children: {
        [key: string]: {
          id: number;
          enquired: boolean;
          waitlisted: boolean;
          book_form_initiated: boolean;
          book_form_completed: boolean;
          payment_initiated: boolean;
          payment_completed: boolean;
          label:
            | "Book"
            | "Continue to Book"
            | "Continue to Pay"
            | "View Bookings"
            | "Join Waitlist"
            | "Waitlisted";
          link: string;
        };
      };
    };
  };
};

interface UserHistory {
  id: number;
  created_at: string;
  user_id: string | null;
  user_uuid: string;
  enquired: number[];
  waitlisted: number[];
  getHelpClicked: string[];
  book_form_initiated: number[];
  book_form_completed: number[];
  payment_completed: number[];
  payment_initiated: number[];
  inventoriesData: InventoryData;
  isInventoryDataLoaded: boolean;
  redirection_urls: {
    book_form_initiated: redirectionURL;
    book_form_completed: redirectionURL;
    payment_initiated: redirectionURL;
    payment_completed: redirectionURL;
  };
  aeroInvData: {
    name: string;
    canonical_name: string;
    price: string | number;
    currency: string;
    id: string;
    img: string;
    label: string;
    link: string;
  } | null;
  isFetched: boolean;
}

const initialState: UserHistory = {
  id: 0,
  created_at: "",
  user_id: "",
  user_uuid: "",
  enquired: [],
  waitlisted: [],
  getHelpClicked: [],
  book_form_initiated: [],
  book_form_completed: [],
  payment_initiated: [],
  payment_completed: [],
  inventoriesData: {
    id: 0,
    enquired: false,
    book_form_initiated: null,
    book_form_completed: null,
    payment_initiated: null,
    payment_completed: null,
    children: {},
  },
  isInventoryDataLoaded: false,
  redirection_urls: {
    book_form_initiated: {},
    book_form_completed: {},
    payment_initiated: {},
    payment_completed: {},
  },
  aeroInvData: null,
  isFetched: false,
};

const userHistory = createSlice({
  name: "userHistory",
  initialState,
  reducers: {
    updateUserHistoryData: (state, { payload }) => {
      state.id = payload.id;
      state.created_at = payload.created_at;
      state.user_id = payload.user_id;
      state.user_uuid = payload.user_uuid;
      state.enquired = payload.enquired;
      state.waitlisted = payload.waitlisted;
      state.getHelpClicked = payload.getHelpClicked;
      state.book_form_initiated = payload.book_form_initiated;
      state.book_form_completed = payload.book_form_completed;
      state.payment_completed = payload.payment_completed;
      state.redirection_urls = payload.redirection_urls;
      state.payment_initiated = payload.payment_initiated;
      state.isFetched = true;
    },

    updateEnquire: (state, { payload }) => {
      state.enquired = [...state.enquired, payload.inventoryId];
    },

    updateBookFormInitiated: (state, { payload }) => {
      state.book_form_initiated = [payload.inventoryId, payload.id, ...state.book_form_initiated];
      state.redirection_urls = {
        ...state.redirection_urls,
        book_form_initiated: {
          ...state.redirection_urls.book_form_initiated,
          [payload.inventoryId]: payload.updatedBookFormInitiatedData,
        },
      };
    },

    updateBookFormCompleted: (state, { payload }) => {
      state.book_form_completed = [payload.inventoryId, payload.id, ...state.book_form_completed];
      state.redirection_urls = {
        ...(state.redirection_urls || {
          book_form_initiated: {},
          book_form_completed: {},
          payment_initiated: {},
        }),
        book_form_completed: {
          ...(state?.redirection_urls?.book_form_completed || {}),
          [payload.inventoryId]: payload.updatedBookFormCompletedData,
        },
      };
    },

    updatePaymentFormInitiated: (state, { payload }) => {
      state.payment_initiated = [payload.inventoryId, payload.id, ...state.payment_initiated];
      state.redirection_urls = {
        ...state.redirection_urls,
        payment_initiated: {
          ...state.redirection_urls.payment_initiated,
          [payload.inventoryId]: payload.updatePaymentFormInitiatedData,
        },
      };
    },

    updatePaymentFormCompleted: (state, { payload }) => {
      state.payment_completed = [payload.inventoryId, payload.id, ...state.payment_completed];
      state.redirection_urls = {
        ...state.redirection_urls,
        payment_completed: {
          ...state.redirection_urls.payment_completed,
          [payload.inventoryId]: payload.updatePaymentFormCompletedData,
        },
      };
    },

    updatePropertyData: (state, { payload }) => {
      const updatedInventory = {
        ...state.inventoriesData,
        id: payload.inventoryId,
        enquired: true,
        ...(payload.book_form_initiated && { book_form_initiated: payload.book_form_initiated }),
        ...(payload.book_form_completed && {
          book_form_completed: payload.book_form_completed,
        }),
        ...(payload.payment_initiated && { payment_initiated: payload.payment_initiated }),
        ...(payload.payment_completed && { payment_completed: payload.payment_completed }),
      };

      state.inventoriesData = { ...updatedInventory };
    },

    updatePropertyRoom: (state, { payload }) => {
      let updatedChildren = {};
      const room = state.inventoriesData.children[payload.roomId];
      if (room) {
        updatedChildren = {
          ...state.inventoriesData.children,
          [payload.roomId]: {
            ...room,
            enquired: true,
            label: payload.label,
            ...(payload.book_form_initiated && { book_form_initiated: true }),
            ...(payload.book_form_completed && { book_form_completed: true }),
            ...(payload.payment_initiated && { payment_initiated: true }),
            ...(payload.payment_completed && { payment_completed: true }),
          },
        };
      } else {
        updatedChildren = {
          ...state.inventoriesData.children,
          [payload.roomId]: {
            id: payload.roomId,
            enquired: true,
            label: payload.label,
            book_form_initiated: null,
            book_form_completed: null,
            payment_initiated: null,
            payment_completed: null,
            children: {},
          },
        };
      }

      state.inventoriesData = {
        ...state.inventoriesData,
        ...(payload.inventoryId && { id: payload.inventoryId }),
        ...(payload.bookFormInitiated && { book_form_initiated: payload.bookFormInitiated }),
        children: updatedChildren,
      };
    },

    updatePropertyTenancy: (state, { payload }) => {
      let updatedChildren = {};
      const room = state.inventoriesData.children[payload.roomId];
      if (room) {
        const tenancy = room.children[payload.tenancyId];
        if (tenancy) {
          updatedChildren = {
            ...state.inventoriesData.children,
            [payload.roomId]: {
              ...room,
              children: {
                ...room.children,
                [payload.tenancyId]: {
                  ...tenancy,
                  enquired: true,
                  label: payload.label,
                  ...(payload.book_form_initiated && { book_form_initiated: true }),
                  ...(payload.book_form_completed && { book_form_completed: true }),
                  ...(payload.payment_initiated && { payment_initiated: true }),
                  ...(payload.payment_completed && { payment_completed: true }),
                },
              },
            },
          };
        } else {
          updatedChildren = {
            ...state.inventoriesData.children,
            [payload.roomId]: {
              ...room,
              children: {
                ...room.children,
                [payload.tenancyId]: {
                  id: payload.tenancyId,
                  enquired: true,
                  label: payload.label,
                  book_form_initiated: null,
                  book_form_completed: null,
                  payment_initiated: null,
                  payment_completed: null,
                },
              },
            },
          };
        }
      } else {
        updatedChildren = {
          ...state.inventoriesData.children,
          [payload.roomId]: {
            id: payload.roomId,
            enquired: false,
            book_form_initiated: null,
            book_form_completed: null,
            payment_initiated: null,
            payment_completed: null,
            label: payload.roomLabel,
            children: {
              [payload.tenancyId]: {
                id: payload.tenancyId,
                enquired: true,
                label: payload.label,
                book_form_initiated: null,
                book_form_completed: null,
                payment_initiated: null,
                payment_completed: null,
              },
            },
          },
        };
      }

      state.inventoriesData = {
        ...state.inventoriesData,
        ...(payload.inventoryId && { id: payload.inventoryId }),
        ...(payload.bookFormInitiated && { book_form_initiated: payload.bookFormInitiated }),
        children: updatedChildren,
      };
    },

    updateGetHelp: (state, { payload }) => {
      state.getHelpClicked = [...state.getHelpClicked, payload.regionCanonicalName];
    },

    updateInventoriesData: (state, { payload }) => {
      const rooms = payload.children.reduce((acc: any, currRoom: any) => {
        if (currRoom.id) {
          return {
            ...acc,
            [`${currRoom.id}`]: {
              ...currRoom,
              children: currRoom.children.reduce((childAcc: any, currTenancy: any) => {
                if (currTenancy.id) return { ...childAcc, [`${currTenancy.id}`]: currTenancy };
                return childAcc;
              }, {}),
            },
          };
        }
        return acc;
      }, {});

      state.inventoriesData = { ...payload, children: rooms };
      state.isInventoryDataLoaded = true;
    },

    updateAeroInvData: (state, { payload }) => {
      state.aeroInvData = payload;
    },

    updateInventoriesDataLoaded: (state, { payload }) => {
      state.isInventoryDataLoaded = payload;
    },
  },
});

export default userHistory.reducer;
export const {
  updateUserHistoryData,
  updateEnquire,
  updateGetHelp,
  updateInventoriesData,
  updatePropertyData,
  updatePropertyRoom,
  updatePropertyTenancy,
  updateInventoriesDataLoaded,
  updateAeroInvData,
  updateBookFormInitiated,
  updateBookFormCompleted,
  updatePaymentFormInitiated,
  updatePaymentFormCompleted,
} = userHistory.actions;
